import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  Home,
  Building2,
  BookOpen,
  MapPin,
  BusFront,
  Headset,
  Sun,
  Moon,
  Menu,
  X,
  User,
  LogOut,
  LogIn,
  UserPlus,
} from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import ProfileAvatar from "./ProfileAvatar";

const NAV_KEYS = [
  { key: "home", defaultText: "Главная", to: "/", icon: Home },
  { key: "cities", defaultText: "Города", to: "/cities", icon: Building2 },
  { key: "guides", defaultText: "Гиды", to: "/guides", icon: BookOpen },
  { key: "attractions", defaultText: "Места", to: "/places", icon: MapPin },
  { key: "tickets", defaultText: "Поездки", to: "/tickets", icon: BusFront },
  { key: "support", defaultText: "Поддержка", to: "/contact", icon: Headset },
];

const BOTTOM_NAV_KEYS = ["home", "tickets", "guides"];

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-1.5 px-2.5 2xl:px-3 py-1.5 rounded-full text-[13px] 2xl:text-sm font-semibold transition-colors shrink-0 ${
    isActive
      ? "bg-amber-400 text-slate-950 shadow-sm"
      : "text-slate-700 dark:text-slate-200 hover:text-slate-950 dark:hover:text-white hover:bg-white/80 dark:hover:bg-slate-700/80"
  }`;

const mobileNavLinkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-3.5 rounded-lg text-[15px] font-semibold transition-colors ${
    isActive
      ? "bg-amber-400 text-slate-950"
      : "text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800 hover:text-slate-950 dark:hover:text-white"
  }`;

const bottomNavLinkClass = ({ isActive }) =>
  `flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-[10px] font-semibold transition-colors ${
    isActive
      ? "bg-amber-400 text-slate-950"
      : "text-slate-500 dark:text-amber-100/60 hover:text-amber-600 dark:hover:text-amber-300"
  }`;

const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [mobileKeyboardOpen, setMobileKeyboardOpen] = useState(false);
  const [mobileNavHidden, setMobileNavHidden] = useState(false);
  const isDriver = user?.role === "driver";
  const isGuide = user?.role === "guide";
  const isRestrictedProvider = (isDriver || isGuide) && !user?.roles?.includes("client");
  const navItems = isRestrictedProvider
    ? [{ key: "applications", defaultText: "Requests", to: "/dashboard", icon: BusFront }]
    : NAV_KEYS;
  const mobileMenuItems = navItems.filter((item) => !["home", "guides", "support", "tickets"].includes(item.key));
  const bottomNavItems = BOTTOM_NAV_KEYS.map((key) => NAV_KEYS.find((item) => item.key === key)).filter(Boolean);

  const isDark = theme === "dark";

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const updateKeyboardState = () => {
      const keyboardOpen = viewport
        ? window.innerHeight - viewport.height > 160
        : Boolean(document.activeElement?.matches?.("input, textarea, select, [contenteditable='true']"));
      setMobileKeyboardOpen(keyboardOpen);
      document.body.classList.toggle("mobile-keyboard-open", keyboardOpen);
      document.documentElement.style.setProperty("--mobile-visual-viewport-height", `${viewport?.height || window.innerHeight}px`);
      document.documentElement.style.setProperty("--mobile-visual-viewport-top", `${viewport?.offsetTop || 0}px`);
    };
    const scheduleKeyboardStateUpdate = () => window.requestAnimationFrame(updateKeyboardState);

    document.addEventListener("focusin", scheduleKeyboardStateUpdate);
    document.addEventListener("focusout", scheduleKeyboardStateUpdate);
    window.addEventListener("resize", scheduleKeyboardStateUpdate);
    viewport?.addEventListener("resize", scheduleKeyboardStateUpdate);
    viewport?.addEventListener("scroll", scheduleKeyboardStateUpdate);
    updateKeyboardState();

    return () => {
      document.removeEventListener("focusin", scheduleKeyboardStateUpdate);
      document.removeEventListener("focusout", scheduleKeyboardStateUpdate);
      window.removeEventListener("resize", scheduleKeyboardStateUpdate);
      viewport?.removeEventListener("resize", scheduleKeyboardStateUpdate);
      viewport?.removeEventListener("scroll", scheduleKeyboardStateUpdate);
      document.body.classList.remove("mobile-keyboard-open");
      document.documentElement.style.removeProperty("--mobile-visual-viewport-height");
      document.documentElement.style.removeProperty("--mobile-visual-viewport-top");
    };
  }, []);

  useEffect(() => {
    let previousScrollY = window.scrollY;
    let frameId = 0;
    const mobileViewport = window.matchMedia("(max-width: 1279px)");

    const updateNavigationVisibility = () => {
      frameId = 0;
      const currentScrollY = window.scrollY;

      if (!mobileViewport.matches || mobileOpen || mobileKeyboardOpen || logoutConfirmOpen) {
        setMobileNavHidden(false);
      } else if (currentScrollY <= 96) {
        setMobileNavHidden(false);
      } else if (currentScrollY - previousScrollY > 5) {
        setMobileNavHidden(true);
      } else if (previousScrollY - currentScrollY > 5) {
        setMobileNavHidden(false);
      }

      previousScrollY = currentScrollY;
    };

    const handleScroll = () => {
      if (!frameId) frameId = window.requestAnimationFrame(updateNavigationVisibility);
    };

    const handleResize = () => {
      previousScrollY = window.scrollY;
      if (!mobileViewport.matches) setMobileNavHidden(false);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [mobileKeyboardOpen, mobileOpen, logoutConfirmOpen]);

  useEffect(() => {
    if (!logoutConfirmOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setLogoutConfirmOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [logoutConfirmOpen]);

  const confirmLogout = async () => {
    setLogoutConfirmOpen(false);
    setMobileOpen(false);
    await logout();
  };

  return (
    <>
    <header
      inert={mobileNavHidden}
      className={`sticky top-0 z-50 bg-white/90 dark:bg-[#0d1016]/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 transition-[transform,background-color,border-color] duration-300 ease-out will-change-transform motion-reduce:transition-none xl:translate-y-0 ${mobileNavHidden ? "-translate-y-full" : "translate-y-0"}`}
    >
      <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 py-3.5 max-w-[1440px] mx-auto gap-2">

        {/* Логотип */}
        <Link to={isRestrictedProvider ? "/dashboard" : "/"} className="flex shrink-0 items-center gap-1.5 group cursor-pointer min-[380px]:gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 shadow-sm shadow-amber-200 transition-transform group-hover:rotate-6 dark:shadow-none min-[380px]:h-9 min-[380px]:w-9">
            <BusFront className="h-4 w-4 text-white min-[380px]:h-5 min-[380px]:w-5" strokeWidth={2.25} />
          </span>
          <span className="text-lg font-extrabold tracking-tight min-[380px]:text-xl sm:text-2xl">
            <span className="text-amber-500 dark:text-amber-400">Uz</span>
            <span className="text-slate-900 dark:text-white">Travel</span>
          </span>
        </Link>

        {/* Навигация для десктопа (показывается от xl / 1280px) */}
        {!isRestrictedProvider && <nav className="hidden xl:flex items-center gap-0.5 bg-amber-50/70 dark:bg-slate-800/80 border border-amber-100 dark:border-slate-700 rounded-full px-1.5 py-1 transition-colors">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                className={navLinkClass}
              >
                {({ isActive }) => (
                  <>
                    <Icon className={`w-3.5 h-3.5 ${isActive ? "text-slate-950" : "text-amber-500"}`} strokeWidth={2.25} />
                    {t(`nav.${item.key}`, item.defaultText)}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>}

        {/* Элементы управления справа */}
        <div className="flex items-center gap-2 shrink-0">

          {!user && !isRestrictedProvider && <>
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              aria-label={t("auth.login_btn", "Войти")}
              title={t("auth.login_btn", "Войти")}
              className="sm:hidden inline-flex h-10 min-w-[3.5rem] shrink-0 items-center justify-center rounded-xl border border-slate-200 px-2.5 text-[11px] font-bold text-slate-800 transition-colors hover:border-amber-400 dark:border-slate-700 dark:text-slate-100 min-[400px]:px-3 min-[400px]:text-xs"
            >
              {t("auth.login_btn", "Войти")}
            </Link>
            <Link
              to="/register"
              onClick={() => setMobileOpen(false)}
              aria-label={t("nav.sign_up", "Регистрация")}
              title={t("nav.sign_up", "Регистрация")}
              className="sm:hidden inline-flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-xl bg-amber-400 px-2.5 text-[11px] font-bold text-slate-950 transition-colors hover:bg-amber-300 min-[400px]:px-3 min-[400px]:text-xs"
            >
              {t("nav.sign_up", "Регистрация")}
            </Link>
          </>}

          {/* Переключатель темы */}
          {!isRestrictedProvider && <button
            type="button"
            aria-label={t("nav.toggle_theme", "Переключить тему")}
            onClick={toggleTheme}
            className="relative hidden h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 transition-colors hover:border-amber-300 hover:bg-amber-50 dark:border-slate-700 dark:hover:border-amber-400 dark:hover:bg-slate-800 sm:flex"
          >
            <Sun
              className={`absolute w-4 h-4 text-amber-500 transition-all duration-300 ${
                isDark ? "opacity-0 -rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"
              }`}
            />
            <Moon
              className={`absolute w-4 h-4 text-amber-400 transition-all duration-300 ${
                isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-50"
              }`}
            />
          </button>}

          {/* Кнопки авторизации / профиля */}
          <div className={`${isDriver ? "flex" : "hidden sm:flex"} items-center`}>
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/dashboard" aria-label={t("nav.dashboard")} title={t("nav.dashboard")} className="inline-flex h-10 items-center gap-1.5 rounded-full border border-slate-200/60 bg-slate-100 px-3 text-xs font-bold text-slate-800 transition-colors hover:border-amber-300 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-amber-400/50 dark:hover:bg-slate-700">
                  <ProfileAvatar avatarUrl={user.avatar_url} name={user.name} className="h-6 w-6 shrink-0" textClassName="text-[9px]" />
                  <span className={isRestrictedProvider ? "hidden md:inline" : ""}>{user.name}</span>
                </Link>
                <button
                  type="button"
                  onClick={() => setLogoutConfirmOpen(true)}
                  title={t("auth.logout", "Выйти")}
                  aria-label={t("auth.logout", "Выйти")}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-500 dark:border-slate-700 dark:hover:border-red-900/50 dark:hover:bg-red-950/20"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="h-10 flex items-center gap-1.5 px-4 rounded-full border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/60 hover:border-amber-400 dark:hover:border-amber-400 text-slate-800 dark:text-slate-100 text-xs sm:text-sm font-semibold transition-colors shrink-0"
                >
                  <LogIn className="w-4 h-4 text-amber-500" />
                  {t("auth.login_btn", "Войти")}
                </Link>
                <Link
                  to="/register"
                  className="h-10 flex items-center gap-1.5 px-4 rounded-full bg-amber-400 hover:bg-amber-500 text-slate-950 text-xs sm:text-sm font-bold transition-colors shadow-sm shadow-amber-500/20 shrink-0"
                >
                  <UserPlus className="w-4 h-4" />
                  {t("nav.sign_up", "Регистрация")}
                </Link>
              </div>
            )}
          </div>

          {/* Переключатель языка */}
          {!isRestrictedProvider && <div className="hidden sm:block"><LanguageSwitcher /></div>}

        </div>
      </div>
    </header>

      {/* Мобильная панель с остальными разделами */}
      {!isRestrictedProvider && mobileOpen && (
        <div className="xl:hidden fixed inset-0 z-[70]">
          <button
            type="button"
            aria-label={t("nav.close_menu", "Закрыть меню")}
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm"
          />
          <nav id="mobile-navigation" className="absolute right-3 top-[calc(4.5rem+env(safe-area-inset-top))] max-h-[calc(100dvh-6rem-env(safe-area-inset-top))] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-[#0d1016]">
            <div className="flex items-center justify-between gap-3 pb-4 mb-3 border-b border-slate-100 dark:border-slate-800">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                {user ? user.name : t("nav.settings", "Настройки")}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label={t("nav.toggle_theme", "Переключить тему")}
                  onClick={toggleTheme}
                  className="relative h-10 w-10 shrink-0 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden hover:border-amber-300 dark:hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Sun
                    className={`absolute w-4 h-4 text-amber-500 transition-all duration-300 ${
                      isDark ? "opacity-0 -rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"
                    }`}
                  />
                  <Moon
                    className={`absolute w-4 h-4 text-amber-400 transition-all duration-300 ${
                      isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-50"
                    }`}
                  />
                </button>
                <LanguageSwitcher />

                {user && (
                  <button
                    onClick={() => setLogoutConfirmOpen(true)}
                    aria-label={t("auth.logout", "Выйти")}
                    className="h-10 w-10 shrink-0 rounded-full text-red-500 bg-red-50 dark:bg-red-950/30 inline-flex items-center justify-center"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  aria-label={t("nav.close_menu", "Закрыть меню")}
                  onClick={() => setMobileOpen(false)}
                  className="h-10 w-10 shrink-0 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-700 dark:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              {mobileMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setMobileOpen(false)}
                    className={mobileNavLinkClass}
                  >
                    <Icon className="w-4.5 h-4.5 text-amber-500 shrink-0" strokeWidth={2.25} />
                    {t(`nav.${item.key}`, item.defaultText)}
                  </NavLink>
                );
              })}
            </div>

            {user && (
              <Link
                to="/dashboard"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3.5 rounded-lg text-[15px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-amber-50 dark:hover:bg-slate-800"
              >
                <User className="w-4.5 h-4.5 text-amber-500" />
                {t("nav.dashboard")}
              </Link>
            )}

          </nav>
        </div>
      )}

      {/* Постоянная нижняя навигация на телефонах и планшетах */}
      {!isRestrictedProvider && !mobileKeyboardOpen && <nav
        inert={mobileNavHidden}
        className={`xl:hidden fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-[60] mx-auto grid max-w-md grid-cols-4 gap-1 rounded-2xl border border-slate-200/80 dark:border-amber-400/20 bg-white/95 dark:bg-[#0d1016]/95 p-1.5 shadow-2xl shadow-slate-950/20 backdrop-blur-xl transition-transform duration-300 ease-out will-change-transform motion-reduce:transition-none ${mobileNavHidden ? "translate-y-[calc(100%+1rem+env(safe-area-inset-bottom))] pointer-events-none" : "translate-y-0"}`}
      >
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setMobileOpen(false)}
              className={bottomNavLinkClass}
            >
              <Icon className="h-5 w-5" strokeWidth={2.1} />
              <span className="w-full truncate text-center">{t(`nav.${item.key}`, item.defaultText)}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          aria-label={mobileOpen ? t("nav.close_menu", "Закрыть меню") : t("nav.toggle_menu", "Открыть меню")}
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
          className={bottomNavLinkClass({ isActive: mobileOpen })}
        >
          {mobileOpen ? <X className="h-5 w-5" strokeWidth={2.1} /> : <Menu className="h-5 w-5" strokeWidth={2.1} />}
          <span className="w-full truncate text-center">{t("nav.menu", "Меню")}</span>
        </button>
      </nav>}

      {!isRestrictedProvider && !mobileKeyboardOpen && !mobileOpen && <Link
        to="/contact"
        onClick={() => setMobileOpen(false)}
        aria-label={t("nav.support", "Поддержка")}
        title={t("nav.support", "Поддержка")}
        className={`xl:hidden fixed right-4 z-[61] inline-flex h-14 w-14 items-center justify-center rounded-full border border-amber-300 bg-amber-400 text-slate-950 shadow-xl shadow-amber-950/25 transition-[bottom,transform] duration-300 ease-out hover:scale-105 motion-reduce:transition-none ${mobileNavHidden ? "bottom-[calc(0.75rem+env(safe-area-inset-bottom))]" : "bottom-[calc(6.25rem+env(safe-area-inset-bottom))]"}`}
      >
        <Headset className="h-6 w-6" strokeWidth={2.25} aria-hidden="true" />
      </Link>}

      {logoutConfirmOpen && (
        <div
          className="mobile-viewport-overlay fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => setLogoutConfirmOpen(false)}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="logout-confirm-title"
            aria-describedby="logout-confirm-description"
            className="mobile-viewport-panel w-full max-w-sm overflow-y-auto rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-[#111722]"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-400/10 dark:text-red-300">
              <LogOut className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2 id="logout-confirm-title" className="mt-4 text-xl font-black text-slate-950 dark:text-white">
              {t("auth.logout_confirm_title", "Выйти из аккаунта?")}
            </h2>
            <p id="logout-confirm-description" className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {t("auth.logout_confirm_text", "Вы уверены, что хотите выйти из аккаунта?")}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                autoFocus
                onClick={() => setLogoutConfirmOpen(false)}
                className="min-h-11 rounded-full border border-slate-200 px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                {t("auth.logout_cancel", "Отмена")}
              </button>
              <button
                type="button"
                onClick={confirmLogout}
                className="inline-flex min-h-11 items-center gap-2 rounded-full bg-red-600 px-4 text-sm font-bold text-white transition hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                {t("auth.logout_confirm", "Выйти")}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default Navbar;
